import { useState } from 'react';
import {
    CheckCircle,
    Trophy,
    Timer,
    ArrowRight,
    Trash,
} from '@phosphor-icons/react';
import { STYLE_GUIDE } from '@/constants/testIds';
import { ExerciseCardPreview } from '@/components/ExerciseCardPreview';

const darkSwatches = [
    { name: 'Charcoal', hex: '#121212', role: 'Background', text: '#F5F5F7' },
    { name: 'Carbon', hex: '#1C1C1C', role: 'Card', text: '#F5F5F7' },
    { name: 'Off-White', hex: '#F5F5F7', role: 'Foreground', text: '#121212' },
    { name: 'Electric Neon', hex: '#00FF9D', role: 'Primary · Data', text: '#121212' },
    { name: 'PR Red', hex: '#FF4D4D', role: 'Destructive · PR', text: '#FFFFFF' },
    { name: 'Steel', hex: '#A0A0A0', role: 'Muted FG', text: '#121212' },
];

const lightSwatches = [
    { name: 'Off-White', hex: '#F5F5F7', role: 'Background', text: '#121212' },
    { name: 'Paper', hex: '#FFFFFF', role: 'Card', text: '#121212' },
    { name: 'Charcoal', hex: '#121212', role: 'Foreground', text: '#F5F5F7' },
    { name: 'Midnight Navy', hex: '#1A1F36', role: 'Primary · Actions', text: '#F5F5F7' },
    { name: 'PR Red', hex: '#FF4D4D', role: 'Destructive · PR', text: '#FFFFFF' },
    { name: 'Slate', hex: '#666666', role: 'Muted FG', text: '#F5F5F7' },
];

export default function StyleGuide() {
    return (
        <main
            data-testid={STYLE_GUIDE.root}
            className="mx-auto max-w-6xl px-5 sm:px-8 pb-32 pt-10 sm:pt-14"
        >
            {/* Title */}
            <div className="animate-fade-up">
                <span className="uppercase-mono text-muted-foreground">
                    Phase 01 / Style guide
                </span>
                <h1 className="mt-3 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
                    The visual system,
                    <br />
                    <span className="text-muted-foreground">laid bare.</span>
                </h1>
                <p className="mt-4 max-w-2xl text-muted-foreground">
                    A living reference for every token, component, and motion
                    rule used across GymFlow. Toggle the theme in the header to
                    inspect contextual styles; the static swatch grids below
                    show both modes regardless of theme.
                </p>
            </div>

            {/* COLORS */}
            <Section
                index="01"
                title="Color"
                kicker="Dual-mode palette"
                testId={STYLE_GUIDE.colorsSection}
            >
                <div className="grid lg:grid-cols-2 gap-5">
                    <PalettePanel
                        mode="dark"
                        swatches={darkSwatches}
                        title="Dark mode · Primary"
                        sub="Matte charcoal canvas + electric neon."
                    />
                    <PalettePanel
                        mode="light"
                        swatches={lightSwatches}
                        title="Light mode · Secondary"
                        sub="Crisp off-white + midnight navy actions."
                    />
                </div>
            </Section>

            {/* TYPOGRAPHY */}
            <Section
                index="02"
                title="Typography"
                kicker="Manrope · JetBrains Mono"
                testId={STYLE_GUIDE.typographySection}
            >
                <div className="glass-card rounded-3xl p-6 sm:p-10 space-y-8">
                    <TypeRow
                        label="H1 / Display"
                        cls="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tighter leading-[0.95]"
                        sample="Push past failure."
                    />
                    <TypeRow
                        label="H2 / Section"
                        cls="text-3xl sm:text-4xl font-display font-bold tracking-tight"
                        sample="Working sets"
                    />
                    <TypeRow
                        label="H3 / Card"
                        cls="text-xl sm:text-2xl font-display font-bold tracking-tight"
                        sample="Barbell Bench Press"
                    />
                    <TypeRow
                        label="Body"
                        cls="text-base leading-relaxed"
                        sample="Log every rep with intent. The numbers compound, the body adapts."
                    />
                    <TypeRow
                        label="Mono / Stat"
                        cls="font-mono text-3xl font-bold tabular-nums tracking-tight"
                        sample="127.5 kg × 5"
                    />
                    <TypeRow
                        label="Label / Mono caps"
                        cls="uppercase-mono"
                        sample="REST · 01:45 · INCOMING"
                    />
                </div>
            </Section>

            {/* BUTTONS */}
            <Section
                index="03"
                title="Buttons"
                kicker="48px+ touch targets, tactile feedback"
                testId={STYLE_GUIDE.buttonsSection}
            >
                <div className="glass-card rounded-3xl p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            data-testid={STYLE_GUIDE.btnPrimary}
                            className="inline-flex items-center gap-2 rounded-2xl px-6 h-12 font-bold tracking-tight bg-primary text-primary-foreground shadow-neon dark:shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                        >
                            Primary action
                            <ArrowRight size={18} weight="bold" />
                        </button>
                        <button
                            data-testid={STYLE_GUIDE.btnSecondary}
                            className="inline-flex items-center gap-2 rounded-2xl px-6 h-12 font-semibold border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all"
                        >
                            Secondary
                        </button>
                        <button
                            data-testid={STYLE_GUIDE.btnGhost}
                            className="inline-flex items-center gap-2 rounded-2xl px-6 h-12 font-semibold text-muted-foreground hover:text-foreground hover:bg-foreground/5 active:scale-[0.98] transition-all"
                        >
                            Ghost
                        </button>
                        <button
                            data-testid={STYLE_GUIDE.btnDestructive}
                            className="inline-flex items-center gap-2 rounded-2xl px-6 h-12 font-bold tracking-tight bg-[#FF4D4D] text-white shadow-pr hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                        >
                            <Trash size={18} weight="bold" />
                            Destructive
                        </button>
                    </div>

                    <div className="mt-6 grid sm:grid-cols-2 gap-3">
                        <button className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all">
                            Full-width · Thumb zone
                        </button>
                        <button className="w-full h-14 rounded-2xl border border-foreground/15 bg-foreground/5 font-semibold hover:bg-foreground/10 active:scale-[0.99] transition-all">
                            Cancel
                        </button>
                    </div>
                </div>
            </Section>

            {/* INPUTS */}
            <Section
                index="04"
                title="Inputs"
                kicker="Mono-aware fields for numeric capture"
                testId={STYLE_GUIDE.inputsSection}
            >
                <div className="glass-card rounded-3xl p-6 sm:p-8 grid sm:grid-cols-2 gap-4">
                    <FieldNumber
                        testId={STYLE_GUIDE.inputWeight}
                        label="Weight"
                        unit="kg"
                        defaultValue="70"
                    />
                    <FieldNumber
                        testId={STYLE_GUIDE.inputReps}
                        label="Reps"
                        unit="x"
                        defaultValue="10"
                    />
                    <div className="sm:col-span-2">
                        <label className="uppercase-mono text-muted-foreground block mb-2">
                            Note
                        </label>
                        <input
                            type="text"
                            placeholder="Felt strong on the last rep…"
                            className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/70"
                        />
                    </div>
                </div>
            </Section>

            {/* BADGES */}
            <Section
                index="05"
                title="Badges & status"
                kicker="Completed · PR · Rest"
                testId={STYLE_GUIDE.badgesSection}
            >
                <div className="glass-card rounded-3xl p-6 sm:p-8 flex flex-wrap gap-3">
                    <Badge color="#00FF9D" icon={<CheckCircle size={12} weight="fill" />}>
                        Set complete
                    </Badge>
                    <Badge color="#FF4D4D" icon={<Trophy size={12} weight="fill" />}>
                        Personal record
                    </Badge>
                    <Badge muted icon={<Timer size={12} weight="bold" />}>
                        Resting · 01:45
                    </Badge>
                    <Badge color="#00FF9D" outline>
                        Warm-up
                    </Badge>
                    <Badge muted>Deload week</Badge>
                </div>
            </Section>

            {/* CARDS */}
            <Section
                index="06"
                title="Card system"
                kicker="Glass over solid, tactile depth"
                testId={STYLE_GUIDE.cardsSection}
            >
                <div className="grid lg:grid-cols-3 gap-5">
                    <DemoCard
                        title="Glass card"
                        body="bg-white/5 backdrop-blur-xl border border-white/10. Default container for exercises, summaries, and metrics."
                        chipText="Default"
                    />
                    <DemoCard
                        title="Solid card"
                        body="Carbon (#1C1C1C) / Paper (#FFFFFF). Use for nested content, forms, and modals where opacity stacking would muddy contrast."
                        chipText="Nested"
                        solid
                    />
                    <DemoCard
                        title="Accent card"
                        body="A thin neon border + tinted background mark the active or highlighted item. Reserve for one focused element per view."
                        chipText="Active"
                        accent
                    />
                </div>
            </Section>

            {/* MOCKUP */}
            <Section
                index="07"
                title="Exercise card · in context"
                kicker="The system, applied"
                testId={STYLE_GUIDE.exerciseMockupSection}
            >
                <ExerciseCardPreview />
            </Section>

            {/* MOTION */}
            <Section
                index="08"
                title="Motion"
                kicker="Haptic-inspired feedback"
                testId={STYLE_GUIDE.motionSection}
            >
                <MotionDemo />
            </Section>

            {/* Spacing footnote */}
            <footer className="mt-20 border-t border-foreground/10 pt-8 flex flex-wrap items-center justify-between gap-3">
                <span className="uppercase-mono text-muted-foreground">
                    GymFlow · v0.1 · Design language
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                    {new Date().getFullYear()} · Built for the work, not the
                    feed.
                </span>
            </footer>
        </main>
    );
}

/* ---------- Helpers ---------- */

function Section({ index, title, kicker, testId, children }) {
    return (
        <section
            data-testid={testId}
            className="mt-20 sm:mt-24 scroll-mt-24 animate-fade-up"
        >
            <div className="flex items-end justify-between gap-6 mb-6">
                <div>
                    <span className="uppercase-mono text-muted-foreground">
                        {index} · {kicker}
                    </span>
                    <h2 className="mt-2 font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
                        {title}
                    </h2>
                </div>
                <span className="hidden sm:block font-mono text-xs text-muted-foreground">
                    /{index}
                </span>
            </div>
            {children}
        </section>
    );
}

function PalettePanel({ mode, swatches, title, sub }) {
    const isDark = mode === 'dark';
    return (
        <div
            className={`rounded-3xl p-6 sm:p-7 border ${
                isDark
                    ? 'bg-[#121212] border-white/10 text-[#F5F5F7]'
                    : 'bg-[#F5F5F7] border-black/10 text-[#121212]'
            } shadow-tactile`}
        >
            <div className="flex items-center justify-between mb-5">
                <div>
                    <p
                        className={`font-mono uppercase tracking-[0.2em] text-xs ${
                            isDark ? 'text-white/50' : 'text-black/50'
                        }`}
                    >
                        {title}
                    </p>
                    <p
                        className={`mt-1 text-sm ${
                            isDark ? 'text-white/60' : 'text-black/60'
                        }`}
                    >
                        {sub}
                    </p>
                </div>
                <span
                    className={`uppercase-mono rounded-full border px-2.5 py-1 ${
                        isDark
                            ? 'border-white/15 text-white/70'
                            : 'border-black/15 text-black/70'
                    }`}
                >
                    {isDark ? 'Dark' : 'Light'}
                </span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
                {swatches.map((s) => (
                    <div
                        key={s.name}
                        className="rounded-2xl p-3 border flex flex-col justify-between min-h-[110px]"
                        style={{
                            background: s.hex,
                            color: s.text,
                            borderColor: 'rgba(127,127,127,0.18)',
                        }}
                    >
                        <span className="uppercase-mono opacity-70">
                            {s.role}
                        </span>
                        <div>
                            <p className="text-sm font-bold tracking-tight">
                                {s.name}
                            </p>
                            <p className="font-mono text-xs opacity-80">
                                {s.hex}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function TypeRow({ label, cls, sample }) {
    return (
        <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:gap-8 items-baseline">
            <span className="uppercase-mono text-muted-foreground">
                {label}
            </span>
            <span className={cls}>{sample}</span>
        </div>
    );
}

function FieldNumber({ testId, label, unit, defaultValue }) {
    const [v, setV] = useState(defaultValue);
    return (
        <div className="min-w-0">
            <label className="uppercase-mono text-muted-foreground block mb-2">
                {label}
            </label>
            <div className="flex items-center gap-2 h-14 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 focus-within:border-foreground/40 transition-colors min-w-0">
                <input
                    data-testid={testId}
                    type="text"
                    inputMode="decimal"
                    size={4}
                    value={v}
                    onChange={(e) => setV(e.target.value)}
                    className="flex-1 min-w-0 w-full bg-transparent outline-none font-mono text-2xl font-bold tabular-nums tracking-tight"
                />
                <span className="uppercase-mono text-muted-foreground shrink-0">
                    {unit}
                </span>
            </div>
        </div>
    );
}

function Badge({ children, color, muted, outline, icon }) {
    if (muted) {
        return (
            <span className="inline-flex items-center gap-1.5 uppercase-mono rounded-full border border-foreground/15 bg-foreground/5 text-muted-foreground px-3 py-1.5">
                {icon}
                {children}
            </span>
        );
    }
    if (outline) {
        return (
            <span
                className="inline-flex items-center gap-1.5 uppercase-mono rounded-full border px-3 py-1.5"
                style={{
                    borderColor: `${color}66`,
                    color,
                }}
            >
                {icon}
                {children}
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1.5 uppercase-mono rounded-full px-3 py-1.5"
            style={{
                background: `${color}22`,
                border: `1px solid ${color}55`,
                color,
            }}
        >
            {icon}
            {children}
        </span>
    );
}

function DemoCard({ title, body, chipText, solid, accent }) {
    const base = 'rounded-3xl p-6 transition-transform hover:-translate-y-[2px]';
    if (accent) {
        return (
            <div
                className={`${base} border bg-[#00FF9D]/5`}
                style={{ borderColor: 'rgba(0,255,157,0.35)' }}
            >
                <span className="uppercase-mono text-[#00FF9D]">{chipText}</span>
                <h3 className="mt-2 text-xl font-display font-bold tracking-tight">
                    {title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {body}
                </p>
            </div>
        );
    }
    if (solid) {
        return (
            <div
                className={`${base} border border-foreground/10 bg-card text-card-foreground`}
            >
                <span className="uppercase-mono text-muted-foreground">
                    {chipText}
                </span>
                <h3 className="mt-2 text-xl font-display font-bold tracking-tight">
                    {title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {body}
                </p>
            </div>
        );
    }
    return (
        <div className={`${base} glass-card`}>
            <span className="uppercase-mono text-muted-foreground">
                {chipText}
            </span>
            <h3 className="mt-2 text-xl font-display font-bold tracking-tight">
                {title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {body}
            </p>
        </div>
    );
}

function MotionDemo() {
    const [pulsing, setPulsing] = useState(false);
    return (
        <div className="glass-card rounded-3xl p-6 sm:p-8 grid sm:grid-cols-[1fr_auto] items-center gap-6">
            <div>
                <p className="uppercase-mono text-muted-foreground mb-2">
                    Haptic pulse
                </p>
                <h3 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">
                    Tap to mark a set complete.
                </h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-md">
                    A soft, expanding ring confirms the rep was logged — visual
                    stand-in for haptic feedback on mobile. Uses{' '}
                    <code className="font-mono text-xs text-foreground bg-foreground/5 px-1.5 py-0.5 rounded">
                        animate-pulse-ring
                    </code>{' '}
                    on the action target.
                </p>
            </div>
            <button
                type="button"
                onClick={() => {
                    setPulsing(true);
                    setTimeout(() => setPulsing(false), 1600);
                }}
                className={`mx-auto sm:mx-0 grid place-items-center w-20 h-20 rounded-full bg-[#00FF9D] text-[#121212] shadow-neon active:scale-[0.94] transition-transform ${
                    pulsing ? 'animate-pulse-ring' : ''
                }`}
                aria-label="Pulse demo"
            >
                <CheckCircle size={32} weight="fill" />
            </button>
        </div>
    );
}
